"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.userProfileSchema = void 0;
/**
 * Shared response-shape schemas used across oRPC procedures.
 *
 * Each schema here is a `z.object(...)` that some procedure's `.output(...)`
 * embeds. TypeScript types are derived via `z.infer<>` so the schema is the
 * single source of truth — when the schema changes, the type changes
 * automatically and consumers re-typecheck against the new shape.
 *
 * Input field fragments (cross-resource `siloKey`, cursor pagination, etc.)
 * live in `api-schemas.ts`; this file is for output shapes.
 */
const z = __importStar(require("zod"));
/**
 * Public-facing info about a user as exposed by the API — deliberately
 * narrower than the stored `Persona` in `server/legacy-data/stored-types.ts`,
 * and named differently to avoid confusion with it. The API only surfaces the
 * rendered display fields (`name`, `photoUrl`, `title`, `isAffiliated`,
 * `deleted`, `profileKey`); `linked` and the admin name/photo overrides are
 * resolved away by the server before the user crosses the wire. A deleted
 * user still resolves to a real (non-null) profile with `deleted: true`
 * (`server/legacy-data/serverstore.ts`'s `getPersonaAsync` placeholder), so a
 * nullable embedding of this type (e.g. `Comment.author`) is null only when
 * the author couldn't be resolved at all — not for a deleted user — which is
 * why such resources also carry the key as an always-populated sibling field
 * (`Comment.authorKey`) rather than relying on `profileKey` alone.
 *
 * `isAffiliated` is deliberately not named `isAffiliatedToPSM`: it's the
 * server's answer to "does this profile get an affiliation title badge",
 * which today is exactly `Persona.isAffiliatedToPSM` but is free to diverge
 * later (e.g. a future invited-external-expert who has a `title` without
 * being PSM staff) without another wire-schema change. It's omitted rather
 * than sent as `false` — most profiles aren't affiliated, and there's no
 * "not set yet" state distinct from "not affiliated" the way there is for
 * `photoUrl`/`title`.
 *
 * There is no avatar-hue field: the colored-initial fallback avatar shown
 * when there's no `photoUrl` is a deterministic function of `profileKey`
 * (`computeHueForKey` in `@repo/shared/persona-hue`), so clients derive it
 * rather than reading it off the wire.
 *
 * `photoUrl`/`title` accept `null` in addition to being optional so
 * that `profiles.ts`'s `DetailedUserProfile` — which always sends them,
 * using `null` for "not set" — stays a strict extension of this type;
 * nothing embedding a plain `UserProfile` needs to distinguish the two "not
 * set" encodings itself.
 *
 * Output-only (never part of any `.input(...)`) — see the length-limits note
 * in docs/architecture/orpc.md for why only inputs are bounded.
 */
exports.userProfileSchema = z
    .object({
    profileKey: z.string().describe("The profile's unique key within the silo (the same value carried elsewhere as authorKey)."),
    name: z.string().describe("The user's effective display name, with any override already applied."),
    photoUrl: z.string().nullable().optional().describe("URL of the user's avatar photo, or null/omitted if they have none."),
    title: z.string().nullable().optional().describe("The user's title or role, or null/omitted if they have none."),
    isAffiliated: z.boolean().optional().describe('Whether this profile is PSM-affiliated; omitted (not false) when it is not.'),
    deleted: z.boolean().optional().describe('True for a deleted user; omitted for every other profile.'),
})
    .describe("A user's public profile: the display fields shown wherever the user appears.");
//# sourceMappingURL=types.js.map